import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Icon, Popover, SocialMediaIcon } from '@kontentino/ui';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faFolder } from '@fortawesome/pro-regular-svg-icons/faFolder';
import SearchTextInput from 'app/components/SearchTextInput';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { MediaLibraryPageConfig } from 'app/config/mediaLibraryPage';
import {
  AlbumScope,
  isSharedScope,
} from 'app/modules/mediaLibraryPage/utils/albumScope';

export interface PickerProfileOption {
  id: number;
  name: string;
  logoSrc?: string;
  type?: number | null;
  isGlobal?: boolean;
}

interface Props {
  profiles: PickerProfileOption[];
  activeProfileId: AlbumScope;
  onSelect(scope: AlbumScope): void;
}

const MediaLibraryPickerProfileSelect: FC<Props> = ({
  profiles,
  activeProfileId,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const activeProfile = useMemo(
    () => profiles.find((profile) => profile.id === activeProfileId),
    [profiles, activeProfileId],
  );

  const isShared = isSharedScope(activeProfileId);

  const activeSocialMediaType = getSocialMediaTypeForBrandLogo(
    activeProfile?.type ?? undefined,
  );

  const filteredProfiles = useMemo(() => {
    const search = searchValue.toLowerCase().trim();
    if (!search) return profiles;
    return profiles.filter((profile) =>
      profile.name.toLowerCase().includes(search),
    );
  }, [profiles, searchValue]);

  const handleSelect = (scope: AlbumScope) => {
    onSelect(scope);
    setSearchValue('');
    setIsOpen(false);
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild data-cy="media-library-picker_page-select">
        <div className="tw-flex tw-h-[36px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-bg-grayscale-10 tw-px-2">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
            {!isShared && activeSocialMediaType && (
              <SocialMediaIcon
                type={activeSocialMediaType}
                size={20}
                variant="plain"
                className="tw-shrink-0"
              />
            )}
            <span
              translate="no"
              className="tw-truncate tw-text-md tw-text-grayscale-180"
            >
              {isShared
                ? t('mediaLibrary.sharedAlbums.label')
                : activeProfile?.name ?? t('selectProfile')}
            </span>
          </div>
          <Icon icon={faChevronDown} className="tw-shrink-0" />
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="tw-z-[1001] tw-w-[var(--radix-popover-trigger-width)] tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow-2xl"
        >
          <div className="tw-p-1">
            <SearchTextInput
              type="text"
              placeholder={t('search')}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onClear={() => setSearchValue('')}
              className="tw-w-full !tw-border-none"
              data-cy="media-library-picker_page-search"
            />
          </div>
          <div className="tw-flex tw-max-h-[350px] tw-flex-col tw-gap-1 tw-overflow-y-auto tw-p-2">
            <button
              type="button"
              onClick={() =>
                handleSelect(MediaLibraryPageConfig.SHARED_ALBUMS_SCOPE)
              }
              data-cy="media-library-picker_page-option-shared"
              className={clsx(
                'tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-py-2 tw-text-left tw-text-sm tw-transition-colors',
                {
                  'tw-bg-primary-10 tw-text-primary-100': isShared,
                  'tw-text-grayscale-180 hover:tw-bg-grayscale-10': !isShared,
                },
              )}
            >
              <Icon icon={faFolder} className="tw-shrink-0" />
              <span className="tw-flex-1 tw-truncate">
                {t('mediaLibrary.sharedAlbums.label')}
              </span>
            </button>
            {filteredProfiles.map((profile) => {
              const isActive = profile.id === activeProfileId;
              const socialMediaType = getSocialMediaTypeForBrandLogo(
                profile.type ?? undefined,
              );
              return (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => handleSelect(profile.id)}
                  data-cy="media-library-picker_page-option"
                  className={clsx(
                    'tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-py-2 tw-text-left tw-text-sm tw-transition-colors',
                    {
                      'tw-bg-primary-10 tw-text-primary-100': isActive,
                      'tw-text-grayscale-180 hover:tw-bg-grayscale-10':
                        !isActive,
                    },
                  )}
                >
                  {socialMediaType && (
                    <SocialMediaIcon
                      type={socialMediaType}
                      size={20}
                      variant="plain"
                      className="tw-shrink-0"
                    />
                  )}
                  <span translate="no" className="tw-flex-1 tw-truncate">
                    {profile.name}
                  </span>
                </button>
              );
            })}
            {filteredProfiles.length === 0 && (
              <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-grayscale-100">
                {t('mediaLibrary.picker.noProfilesFound')}
              </div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default MediaLibraryPickerProfileSelect;
